import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QrCode, Crown, CheckCircle2, AlertCircle, ScanLine, User } from 'lucide-react';
import { post } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import { Card, Button, Input, Field, Badge, SectionTitle, Avatar, EmptyState } from '../../components/UI';

/**
 * The front desk screen. A USB/Bluetooth barcode scanner behaves as a keyboard,
 * so the input stays focused and submits on Enter — no camera needed.
 */
export default function CheckInDesk() {
  const { eventId } = useEvents();
  const toast = useToast();
  const inputRef = useRef(null);

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  /* Keep focus on the input so a hardware scanner always lands in the field. */
  useEffect(() => {
    const focus = () => inputRef.current?.focus();
    focus();
    const id = setInterval(focus, 2500);
    return () => clearInterval(id);
  }, []);

  const scan = async (e) => {
    e?.preventDefault();
    const value = code.trim();
    if (!value) return;

    setBusy(true);
    setError('');
    try {
      const res = await post('/rooms/scan-checkin', { code: value, ...(eventId ? { event: eventId } : {}) });
      setResult({ ...res.data, message: res.message });
      setHistory((h) => [{ ...res.data, at: new Date(), message: res.message }, ...h].slice(0, 8));
      toast.success(res.message);
    } catch (err) {
      setError(err.message);
      setResult(null);
      toast.error(err.message);
    } finally {
      setBusy(false);
      setCode('');
      inputRef.current?.focus();
    }
  };

  return (
    <>
      <SectionTitle
        title="Check-in desk"
        subtitle="Scan a guest's digital pass to find their room and check them in"
        action={<EventPicker />}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16, alignItems: 'start' }} className="gl-desk">
        {/* ── scanner ──────────────────────────────────────────── */}
        <Card padding={26}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <motion.div
              animate={{ scale: [1, 1.05, 1], opacity: [0.85, 1, 0.85] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                width: 78, height: 78, margin: '0 auto 16px', borderRadius: 'var(--r-xl)',
                background: 'rgba(239,212,138,0.1)', border: '1px solid rgba(239,212,138,0.25)',
                display: 'grid', placeItems: 'center', position: 'relative', overflow: 'hidden',
              }}
            >
              <QrCode size={34} color="var(--gold-400)" />
              <motion.div
                animate={{ y: [-38, 38, -38] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  position: 'absolute', left: 0, right: 0, height: 2,
                  background: 'linear-gradient(90deg, transparent, var(--gold-400), transparent)',
                }}
              />
            </motion.div>
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Ready to scan</h3>
            <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 5 }}>
              Point your barcode scanner at the guest's pass, or type the code below
            </p>
          </div>

          <form onSubmit={scan}>
            <Field label="Guest QR code">
              <Input
                ref={inputRef}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="GL|profile|…"
                style={{ fontFamily: 'monospace', fontSize: 12.5, textAlign: 'center', padding: '13px' }}
                autoFocus
              />
            </Field>
            <Button type="submit" size="lg" icon={ScanLine} loading={busy} style={{ width: '100%', marginTop: 14 }}>
              Look up guest
            </Button>
          </form>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                style={{
                  marginTop: 16, padding: 13, display: 'flex', gap: 11, alignItems: 'flex-start',
                  background: 'var(--danger-dim)', border: '1px solid rgba(248,113,113,0.25)',
                  borderRadius: 'var(--r)',
                }}
              >
                <AlertCircle size={17} color="var(--danger)" style={{ flexShrink: 0, marginTop: 1 }} />
                <p style={{ fontSize: 13, color: 'var(--danger)' }}>{error}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>

        {/* ── result ───────────────────────────────────────────── */}
        <Card padding={26} style={{ minHeight: 340 }}>
          <AnimatePresence mode="wait">
            {result ? (
              <motion.div
                key={result.guest?.id + String(result.checkedIn)}
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 26 }}
              >
                <div style={{ textAlign: 'center', marginBottom: 22 }}>
                  <motion.div
                    initial={{ scale: 0 }} animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 18, delay: 0.08 }}
                    style={{ display: 'inline-block', marginBottom: 13 }}
                  >
                    <Avatar name={result.guest?.name} size={62} vip={result.guest?.isVip} />
                  </motion.div>
                  <h3 style={{ fontSize: 19, fontWeight: 600 }}>{result.guest?.name}</h3>
                  {result.guest?.isVip && (
                    <Badge tone="gold" style={{ marginTop: 8 }}><Crown size={11} /> VIP Guest</Badge>
                  )}
                </div>

                <div style={{
                  padding: 20, textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(239,212,138,0.1), rgba(239,212,138,0.03))',
                  border: '1px solid rgba(239,212,138,0.22)', borderRadius: 'var(--r-lg)',
                }}>
                  <p style={{ fontSize: 11, color: 'var(--text-mid)', textTransform: 'uppercase', letterSpacing: 1.1, fontWeight: 600 }}>
                    Room number
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-display)', fontSize: 42, fontWeight: 700,
                    color: 'var(--gold-300)', lineHeight: 1.15, marginTop: 6,
                  }}>
                    {result.room?.roomNumber || 'Not assigned'}
                  </p>
                  <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 4 }}>
                    {result.room?.category}
                    {result.room?.floor ? ` · Floor ${result.room.floor}` : ''}
                  </p>
                </div>

                {result.room?.occupants?.length > 1 && (
                  <div style={{ marginTop: 18 }}>
                    <p style={{ fontSize: 11, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600, marginBottom: 9 }}>
                      Sharing with
                    </p>
                    {result.room.occupants
                      .filter((o) => o._id !== result.guest?.id)
                      .map((o) => (
                        <div key={o._id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                          <Avatar name={o.name} size={26} vip={o.isVip} />
                          <span style={{ fontSize: 13 }}>{o.name}</span>
                        </div>
                      ))}
                  </div>
                )}

                <div style={{
                  marginTop: 20, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10,
                  background: result.checkedIn ? 'var(--ok-dim)' : 'var(--warn-dim)',
                  border: `1px solid ${result.checkedIn ? 'rgba(74,222,128,0.25)' : 'rgba(251,191,36,0.25)'}`,
                  borderRadius: 'var(--r)',
                }}>
                  <CheckCircle2 size={17} color={result.checkedIn ? 'var(--ok)' : 'var(--warn)'} style={{ flexShrink: 0 }} />
                  <p style={{ fontSize: 12.8, color: result.checkedIn ? 'var(--ok)' : 'var(--warn)' }}>
                    {result.checkedIn
                      ? 'Checked in — the guest has been notified of their room'
                      : 'Assign a room number before checking this guest in'}
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <EmptyState
                  message="Waiting for a scan"
                  hint="The guest's details and room number will appear here as soon as their pass is scanned."
                  icon={User}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      </div>

      {/* ── recent ───────────────────────────────────────────────── */}
      {history.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 14 }}>Recent check-ins</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {history.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 13px',
                  background: 'var(--ink-800)', border: '1px solid var(--border)', borderRadius: 'var(--r)',
                }}
              >
                <Avatar name={h.guest?.name} size={28} vip={h.guest?.isVip} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{h.guest?.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{h.message}</div>
                </div>
                <Badge tone="gold">{h.room?.roomNumber || '—'}</Badge>
              </motion.div>
            ))}
          </div>
        </Card>
      )}

      <style>{`@media (max-width: 940px) { .gl-desk { grid-template-columns: 1fr !important; } }`}</style>
    </>
  );
}
